import { z } from "zod";
import {
  canonicalSmartCreationTargetIntentKey,
  SmartCreationAutoTargetExpressionSchema,
  SmartCreationStrategyEntityIdSchema,
  type SmartCreationStrategyLane,
  SmartCreationStrategyLaneSchema,
  type SmartCreationStrategyTarget,
  type SmartCreationStrategyTargetIntent,
  SmartCreationStrategyTargetIntentSchema,
  SmartCreationStrategyTargetSchema,
  SmartCreationStrategyTargetSourceSchema,
  type SmartCreationTargetingStrategyKind,
  SmartCreationTargetingStrategyKindSchema,
  smartCreationStrategyLaneKind,
} from "./strategy-targets";

export const SmartCreationStrategySeedRuleValueShape = {
  id: SmartCreationStrategyEntityIdSchema,
  dimension: z.enum(["keyword", "asin", "brand"]),
  operator: z.enum(["equals", "contains"]),
  value: z.string().trim().min(1).max(200),
} as const;

export function validateSmartCreationStrategySeedRule(
  rule: { dimension: "keyword" | "asin" | "brand"; operator: "equals" | "contains"; value: string },
  ctx: z.RefinementCtx,
): void {
  if (rule.dimension === "asin" && rule.operator !== "equals") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ASIN seed rules support Equals only",
      path: ["operator"],
    });
  }
  if (rule.dimension === "asin" && !/^[A-Z0-9]{10}$/.test(rule.value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "ASIN seed rules require a valid ASIN",
      path: ["value"],
    });
  }
}

const SmartCreationStrategySeedRuleValueSchema = z
  .object(SmartCreationStrategySeedRuleValueShape)
  .strict()
  .superRefine(validateSmartCreationStrategySeedRule);

export const SmartCreationStrategySeedProvenanceSchema = z.discriminatedUnion("mode", [
  z
    .object({
      mode: z.literal("entered"),
      recordedAt: z.string().datetime().nullable(),
    })
    .strict(),
  z
    .object({
      mode: z.literal("accepted"),
      acceptedAt: z.string().datetime(),
      recommendationId: z.string().uuid(),
      recommendationSeedId: SmartCreationStrategyEntityIdSchema,
      originalRecommendation: z
        .object({
          dimension: z.enum(["keyword", "asin", "brand"]),
          operator: z.enum(["equals", "contains"]),
          value: z.string().trim().min(1).max(200),
          source: SmartCreationStrategyTargetSourceSchema,
          rationale: z.string().trim().min(1).max(1_000),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      mode: z.literal("overridden"),
      acceptedAt: z.string().datetime(),
      overriddenAt: z.string().datetime(),
      recommendationId: z.string().uuid(),
      recommendationSeedId: SmartCreationStrategyEntityIdSchema,
      originalRecommendation: z
        .object({
          dimension: z.enum(["keyword", "asin", "brand"]),
          operator: z.enum(["equals", "contains"]),
          value: z.string().trim().min(1).max(200),
          source: SmartCreationStrategyTargetSourceSchema,
          rationale: z.string().trim().min(1).max(1_000),
        })
        .strict(),
    })
    .strict(),
]);
export type SmartCreationStrategySeedProvenance = z.infer<
  typeof SmartCreationStrategySeedProvenanceSchema
>;

export const SmartCreationStrategySeedRuleSchema = z
  .object({
    ...SmartCreationStrategySeedRuleValueShape,
    provenance: SmartCreationStrategySeedProvenanceSchema,
  })
  .strict()
  .superRefine(validateSmartCreationStrategySeedRule)
  .superRefine((seed, ctx) => {
    if (seed.provenance.mode === "entered") return;
    const original = seed.provenance.originalRecommendation;
    const parsedOriginal = SmartCreationStrategySeedRuleValueSchema.safeParse({
      id: seed.provenance.recommendationSeedId,
      dimension: original.dimension,
      operator: original.operator,
      value: original.value,
    });
    if (!parsedOriginal.success) {
      for (const issue of parsedOriginal.error.issues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: issue.message,
          path: [
            "provenance",
            "originalRecommendation",
            ...issue.path.filter((part) => part !== "id"),
          ],
        });
      }
    }
    const matchesOriginal =
      seed.dimension === original.dimension &&
      seed.operator === original.operator &&
      seed.value === original.value;
    if (seed.provenance.mode === "accepted" && !matchesOriginal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Accepted Seed must match its original recommendation",
        path: ["provenance", "mode"],
      });
    }
    if (seed.provenance.mode === "overridden" && matchesOriginal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Overridden Seed must differ from its original recommendation",
        path: ["provenance", "mode"],
      });
    }
  });
export type SmartCreationStrategySeedRule = z.infer<typeof SmartCreationStrategySeedRuleSchema>;

export const SmartCreationTargetingStrategyBranchSchema = z
  .object({
    kind: SmartCreationTargetingStrategyKindSchema,
    enabled: z.boolean(),
    enabledLanes: z.array(SmartCreationStrategyLaneSchema).max(13).optional(),
    targets: z.array(SmartCreationStrategyTargetSchema).max(500),
    suppressedTargets: z
      .array(
        z
          .object({
            intent: SmartCreationStrategyTargetIntentSchema,
            productAsins: z.array(z.string()).optional(),
          })
          .strict(),
      )
      .max(500)
      .optional(),
    seeds: z.array(SmartCreationStrategySeedRuleSchema).max(100),
  })
  .strict()
  .superRefine((branch, ctx) => {
    if (
      branch.enabledLanes &&
      (new Set(branch.enabledLanes).size !== branch.enabledLanes.length ||
        branch.enabled !== branch.enabledLanes.length > 0 ||
        branch.enabledLanes.some((lane) => smartCreationStrategyLaneKind(lane) !== branch.kind) ||
        (branch.kind === "automatic" && !branch.enabledLanes.includes("automatic")))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enabled lanes must be unique and belong to their Strategy; Automatic is required",
        path: ["enabledLanes"],
      });
    }
    const ids = new Set<string>();
    for (const [index, item] of [...branch.targets, ...branch.seeds].entries()) {
      if (ids.has(item.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Target and seed identities must be unique within a Strategy",
          path: [index < branch.targets.length ? "targets" : "seeds", "id"],
        });
      }
      ids.add(item.id);
    }

    if (branch.kind === "automatic") {
      if (!branch.enabled) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Automatic Strategy is always enabled",
          path: ["enabled"],
        });
      }
      if (branch.seeds.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Automatic Strategy does not accept seed rules",
          path: ["seeds"],
        });
      }
      if (!branch.targets.some((target) => target.included)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Automatic Strategy must include at least one Target group",
          path: ["targets"],
        });
      }
      const expressions = branch.targets.map((target) =>
        target.intent.type === "auto" ? target.intent.expressionType : null,
      );
      if (
        branch.targets.length !== SmartCreationAutoTargetExpressionSchema.options.length ||
        expressions.some((expression) => expression === null) ||
        new Set(expressions).size !== SmartCreationAutoTargetExpressionSchema.options.length
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Automatic Strategy must retain close match, loose match, substitutes and complements",
          path: ["targets"],
        });
      }
      return;
    }

    if (branch.kind !== "branded" && branch.kind !== "competitor" && branch.seeds.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Seed classification rules are supported by Branded and Competitor Strategies",
        path: ["seeds"],
      });
    }

    for (const [index, target] of branch.targets.entries()) {
      if (
        branch.kind !== "research" &&
        target.intent.type === "product" &&
        target.intent.matchType === "expanded"
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Expanded Product Targets belong to Research; other Product lanes require exact intent",
          path: ["targets", index, "intent", "matchType"],
        });
      }
      if (target.intent.type === "auto") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Auto Target intents belong to Automatic Strategy",
          path: ["targets", index, "intent"],
        });
        continue;
      }
      if (branch.kind === "research") {
        const supported =
          target.intent.type === "product" ||
          target.intent.type === "category" ||
          (target.intent.type === "keyword" && target.intent.matchType !== "exact");
        if (!supported) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Research Strategy supports Broad, Phrase, Product and Category Targets",
            path: ["targets", index, "intent"],
          });
        }
      }
      if (
        branch.kind === "performance" &&
        target.intent.type !== "product" &&
        !(target.intent.type === "keyword" && target.intent.matchType === "exact")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Performance Strategy supports Exact Keyword and Product Targets",
          path: ["targets", index, "intent"],
        });
      }
      if (
        (branch.kind === "branded" || branch.kind === "competitor") &&
        target.intent.type !== "keyword" &&
        target.intent.type !== "product" &&
        target.intent.type !== "category_brand"
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Branded and Competitor Strategies support Keyword, Product and Category-brand Targets",
          path: ["targets", index, "intent"],
        });
      }
    }
  });
export type SmartCreationTargetingStrategyBranch = z.infer<
  typeof SmartCreationTargetingStrategyBranchSchema
>;

export function smartCreationStrategyTargetLane(
  kind: SmartCreationTargetingStrategyKind,
  intent: SmartCreationStrategyTargetIntent,
): SmartCreationStrategyLane {
  if (kind === "automatic") return "automatic";
  if (kind === "research") {
    if (intent.type === "category") return "research_category";
    if (intent.type === "product") return "research_product_expanded";
    return intent.type === "keyword" && intent.matchType === "broad"
      ? "research_broad"
      : "research_phrase";
  }
  if (kind === "performance")
    return intent.type === "product" ? "performance_product_exact" : "performance_exact";
  if (kind === "competitor") {
    if (intent.type === "category_brand") return "competitor_brand";
    return intent.type === "product" ? "competitor_product_exact" : "competitor_keywords";
  }
  if (intent.type === "category_brand") return "branded_own_brand";
  return intent.type === "product" ? "branded_own_asins" : "branded_keywords";
}

/** Older plans retain their exact intent and derive selection from their existing targets. */
export function effectiveSmartCreationStrategyLanes(
  branch: SmartCreationTargetingStrategyBranch,
): SmartCreationStrategyLane[] {
  if (branch.kind === "automatic") return ["automatic"];
  if (branch.enabledLanes) return [...branch.enabledLanes];
  if (!branch.enabled) return [];
  return [
    ...new Set(
      branch.targets.map((target) => smartCreationStrategyTargetLane(branch.kind, target.intent)),
    ),
  ];
}

export function isSmartCreationStrategyTargetActive(
  branch: SmartCreationTargetingStrategyBranch,
  target: SmartCreationStrategyTarget,
): boolean {
  return (
    branch.enabled &&
    target.included &&
    effectiveSmartCreationStrategyLanes(branch).includes(
      smartCreationStrategyTargetLane(branch.kind, target.intent),
    )
  );
}

/** Legacy/manual Targets without a scope apply to all selected product groups. */
export function smartCreationStrategyTargetScopesOverlap(
  left: Pick<SmartCreationStrategyTarget, "productAsins">,
  right: Pick<SmartCreationStrategyTarget, "productAsins">,
): boolean {
  return (
    !left.productAsins ||
    !right.productAsins ||
    left.productAsins.some((asin) => right.productAsins!.includes(asin))
  );
}

/** Preserve selected intents when any actual product group still needs Targets. */
export function pendingSmartCreationStrategyLanes(
  branches: readonly SmartCreationTargetingStrategyBranch[],
  products?: readonly { asin: string; variationGroupKey: string }[],
): SmartCreationStrategyLane[] {
  const groups = new Map<string, string[]>();
  for (const product of products ?? [])
    groups.set(product.variationGroupKey, [
      ...(groups.get(product.variationGroupKey) ?? []),
      product.asin,
    ]);
  return branches.flatMap((branch) =>
    branch.kind === "automatic"
      ? []
      : effectiveSmartCreationStrategyLanes(branch).filter((lane) => {
          const targets = branch.targets.filter(
            (target) =>
              isSmartCreationStrategyTargetActive(branch, target) &&
              smartCreationStrategyTargetLane(branch.kind, target.intent) === lane,
          );
          return (
            targets.length === 0 ||
            [...groups.values()].some(
              (asins) =>
                !targets.some(
                  (target) =>
                    !target.productAsins ||
                    target.productAsins.some((asin) => asins.includes(asin)),
                ),
            )
          );
        }),
  );
}

export const SmartCreationTargetingStrategyConfigurationSchema = z
  .object({
    branches: z.array(SmartCreationTargetingStrategyBranchSchema).length(5),
  })
  .strict()
  .superRefine((configuration, ctx) => {
    for (const kind of SmartCreationTargetingStrategyKindSchema.options) {
      const occurrences = configuration.branches.filter((branch) => branch.kind === kind).length;
      if (occurrences !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Strategy configuration requires exactly one ${kind} branch`,
          path: ["branches"],
        });
      }
    }

    const targetOwners = new Map<
      string,
      Array<{ kind: SmartCreationTargetingStrategyKind; target: SmartCreationStrategyTarget }>
    >();
    const seedOwners = new Map<string, SmartCreationTargetingStrategyKind>();
    for (const [branchIndex, branch] of configuration.branches.entries()) {
      for (const [targetIndex, target] of branch.targets.entries()) {
        const intent = target.intent;
        const canonicalIntent = canonicalSmartCreationTargetIntentKey(intent);
        const owners = targetOwners.get(canonicalIntent) ?? [];
        const firstOwner = owners.find(
          (owner) =>
            owner.kind === branch.kind &&
            smartCreationStrategyTargetScopesOverlap(owner.target, target),
        )?.kind;
        if (firstOwner) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              firstOwner === branch.kind
                ? `Target intent is duplicated within the ${branch.kind} Strategy`
                : `Target intent duplicates the ${firstOwner} Strategy`,
            path: ["branches", branchIndex, "targets", targetIndex, "intent"],
          });
        } else {
          targetOwners.set(canonicalIntent, [...owners, { kind: branch.kind, target }]);
        }
      }
      for (const [seedIndex, seed] of branch.seeds.entries()) {
        const canonicalSeed = `${seed.dimension}:${seed.operator}:${seed.value
          .trim()
          .toLocaleLowerCase("en-US")}`;
        const firstOwner = seedOwners.get(canonicalSeed);
        if (firstOwner) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              firstOwner === branch.kind
                ? `Seed rule is duplicated within the ${branch.kind} Strategy`
                : `Seed rule overlaps the ${firstOwner} Strategy`,
            path: ["branches", branchIndex, "seeds", seedIndex],
          });
        } else {
          seedOwners.set(canonicalSeed, branch.kind);
        }
      }
    }
  });
export type SmartCreationTargetingStrategyConfiguration = z.infer<
  typeof SmartCreationTargetingStrategyConfigurationSchema
>;

export function createDefaultSmartCreationTargetingStrategyConfiguration(
  recordedAt: string | null = null,
): SmartCreationTargetingStrategyConfiguration {
  return SmartCreationTargetingStrategyConfigurationSchema.parse({
    branches: [
      {
        kind: "automatic",
        enabled: true,
        targets: SmartCreationAutoTargetExpressionSchema.options.map((expressionType) => ({
          id: `automatic:${expressionType}`,
          intent: { type: "auto", expressionType },
          bid: 0.75,
          included: expressionType === "close_match" || expressionType === "substitutes",
          provenance: { mode: "entered", origin: "system_required", recordedAt },
        })),
        seeds: [],
      },
      { kind: "research", enabled: false, targets: [], seeds: [] },
      { kind: "performance", enabled: false, targets: [], seeds: [] },
      { kind: "branded", enabled: false, targets: [], seeds: [] },
      { kind: "competitor", enabled: false, targets: [], seeds: [] },
    ],
  });
}
